import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { PageHead, Skel, Stat } from '../components/ui'
import { thaiDate } from '../lib/format'
import { useAuth } from '../auth'

type Rag = 'green' | 'amber' | 'red'
type Cadence = 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'event'
type Criticality = 'statutory' | 'regulatory' | 'contractual'

interface Evidence {
  id: string
  obligation_id: string
  period_end: string
  evidence_kind: string
  reference_no: string
  note?: string
  submitted_on: string
  submitted_by?: string
}

interface Assessment {
  id: string
  code: string
  title: string
  authority: string
  legal_ref: string
  category: string
  cadence: Cadence
  due_day?: number
  anchor_on?: string
  period_offset_months: number
  grace_days: number
  criticality: Criticality
  weight: number
  active: boolean
  owner_role?: string
  notes?: string
  next_due_on?: string
  days_until: number
  days_overdue: number
  overdue: boolean
  dormant: boolean
  late: boolean
  rag: Rag
  attainment_bps: number
  last_evidence?: Evidence
}

interface CategoryScore {
  category: string
  score_bps: number
  weight_total: number
  obligations: number
  overdue: number
}

interface Snapshot {
  as_of: string
  score_bps: number
  score_percent: number
  green: number
  amber: number
  red: number
  overdue: number
  total_weight: number
}

interface EvidenceRecord extends Evidence {
  obligation_code: string
  obligation_title: string
}

interface Dashboard {
  score_bps: number
  score_percent: number
  rag: Rag
  weighted_bps: number
  weight_total: number
  total: number
  scored: number
  green: number
  amber: number
  red: number
  overdue: number
  dormant: number
  as_of: string
  obligations_total: number
  categories: CategoryScore[]
  next_deadlines: Assessment[]
  recent_evidence: EvidenceRecord[]
  trend: Snapshot[]
  delta_bps?: number
}

const cadenceLabel: Record<Cadence, string> = {
  monthly: 'รายเดือน',
  quarterly: 'รายไตรมาส',
  semiannual: 'ครึ่งปี',
  annual: 'รายปี',
  event: 'ตามเหตุการณ์',
}

const criticalityLabel: Record<Criticality, string> = {
  statutory: 'ตามกฎหมาย',
  regulatory: 'ตามกฎระเบียบ',
  contractual: 'ตามสัญญา',
}

const categoryLabel: Record<string, string> = {
  data_protection: 'คุ้มครองข้อมูลส่วนบุคคล',
  tax: 'ภาษีอากร',
  labour: 'แรงงานและประกันสังคม',
  reporting: 'การรายงานภาครัฐ',
  retention: 'การเก็บรักษาเอกสาร',
  corporate: 'บรรษัทภิบาล',
}

const kindLabel: Record<string, string> = {
  filing_receipt: 'หลักฐานการยื่นแบบ',
  payment_receipt: 'ใบเสร็จชำระเงิน',
  report: 'รายงาน',
  certificate: 'หนังสือรับรอง',
  policy: 'นโยบาย',
  minutes: 'รายงานการประชุม',
  other: 'อื่น ๆ',
}

const roleLabel: Record<string, string> = {
  owner: 'เจ้าขององค์กร',
  admin: 'ผู้ดูแลระบบ',
  audit_manager: 'ผู้จัดการตรวจสอบ',
  auditor: 'ผู้ตรวจสอบ',
  reviewer: 'ผู้ทบทวน',
}

const ragLabel: Record<Rag, string> = {
  green: 'เป็นไปตามกำหนด',
  amber: 'เฝ้าระวัง',
  red: 'เกินกำหนด',
}

const ragTone: Record<Rag, string> = {
  green: 'sev-low',
  amber: 'sev-high',
  red: 'sev-critical',
}

function RagBadge({ rag }: { rag: Rag }) {
  return (
    <span className={`sev ${ragTone[rag]}`}>
      <i className="sev-dot" />
      {ragLabel[rag]}
    </span>
  )
}

function dueText(row: Assessment): string {
  if (row.dormant) return 'ยังไม่มีการเปิดกำหนด'
  if (row.overdue) return `เกินกำหนด ${row.days_overdue} วัน`
  if (row.days_until < 0) return `เกินกำหนด ${row.days_overdue} วัน · อยู่ในระยะผ่อนผัน`
  if (row.days_until === 0) return 'ครบกำหนดวันนี้'
  return `เหลือ ${row.days_until} วัน`
}

function dueTone(row: Assessment): string {
  if (row.overdue) return 'sev-critical'
  if (row.dormant) return 'dim'
  if (row.days_until <= 7) return 'sev-high'
  return 'sev-low'
}

function cadenceHint(row: Assessment): string {
  if (row.cadence === 'event') return row.dormant ? 'รอเหตุการณ์' : 'นับจากเหตุการณ์'
  if (row.period_offset_months > 0) return `${cadenceLabel[row.cadence]} · วันที่ ${row.due_day} ของเดือนถัดไป`
  return `${cadenceLabel[row.cadence]} · วันที่ ${row.due_day}`
}

function todayISO(): string {
  const now = new Date()
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

function TrendBars({ trend }: { trend: Snapshot[] }) {
  if (trend.length === 0) {
    return <div className="dim" style={{ fontSize: 12 }}>ยังไม่มีจุดบันทึกคะแนนในอดีต ระบบจะบันทึกเมื่อมีการแนบหลักฐาน</div>
  }
  return (
    <div className="row" style={{ alignItems: 'flex-end', gap: 10, height: 132, marginTop: 8 }}>
      {trend.map((s) => {
        const tone = s.score_bps >= 8500 ? 'var(--color-emerald)' : s.score_bps >= 7000 ? 'var(--color-amber)' : 'var(--color-crimson)'
        return (
          <div key={s.as_of} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div className="tnum dim" style={{ fontSize: 10.5 }}>{s.score_percent.toFixed(1)}</div>
            <div style={{
              width: '100%', maxWidth: 46, height: `${18 + (s.score_bps / 10000) * 74}px`,
              background: tone, opacity: 0.82, borderRadius: '3px 3px 0 0',
            }} title={`${s.as_of} · ${s.score_percent.toFixed(2)}%`} />
            <div className="dim tnum" style={{ fontSize: 10 }}>{s.as_of.slice(5)}</div>
          </div>
        )
      })}
    </div>
  )
}

export function Compliance() {
  const qc = useQueryClient()
  const { me } = useAuth()
  const [selected, setSelected] = useState<string | null>(null)
  const [evidenceFor, setEvidenceFor] = useState<Assessment | null>(null)
  const [periodEnd, setPeriodEnd] = useState('')
  const [kind, setKind] = useState('filing_receipt')
  const [referenceNo, setReferenceNo] = useState('')
  const [note, setNote] = useState('')
  const [submittedOn, setSubmittedOn] = useState(todayISO)

  const dashboard = useQuery({
    queryKey: ['compliance', 'dashboard'],
    queryFn: () => api<Dashboard>('/v1/compliance/dashboard'),
    staleTime: 15_000,
  })
  const obligations = useQuery({
    queryKey: ['compliance', 'obligations'],
    queryFn: () => api<Assessment[]>('/v1/compliance/obligations'),
    staleTime: 15_000,
  })

  const attach = useMutation({
    mutationFn: (row: Assessment) => apiWrite<{ evidence: Evidence }>(
      `/v1/compliance/obligations/${row.id}/evidence`, 'POST', {
        period_end: periodEnd,
        evidence_kind: kind,
        reference_no: referenceNo.trim(),
        note: note.trim(),
        submitted_on: submittedOn,
      }),
    onSuccess: () => {
      setEvidenceFor(null)
      setReferenceNo('')
      setNote('')
      void qc.invalidateQueries({ queryKey: ['compliance'] })
    },
  })

  const canAttach = me ? ['owner', 'admin', 'audit_manager', 'auditor'].includes(me.role) : false
  const rows = obligations.data ?? []
  const d = dashboard.data

  const openEvidence = (row: Assessment) => {
    setEvidenceFor(row)
    setPeriodEnd(row.next_due_on ?? '')
    setKind('filing_receipt')
    setReferenceNo('')
    setNote('')
    setSubmittedOn(todayISO())
  }

  const delta = d?.delta_bps
  const scoreTone = d ? (d.rag === 'green' ? 'var(--color-emerald)' : d.rag === 'amber' ? 'var(--color-amber)' : 'var(--color-crimson)') : undefined

  return (
    <>
      <PageHead
        title="การปฏิบัติตามกฎระเบียบ"
        sub="ทะเบียนภาระตามกฎหมายไทยและภาพรวมสำหรับผู้บริหาร · คะแนนถ่วงน้ำหนักตามน้ำหนักความสำคัญของแต่ละภาระ"
        actions={d ? <span className="chip">ณ วันที่ {thaiDate(d.as_of)}</span> : undefined}
      />

      {dashboard.isError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {dashboard.error instanceof Error ? dashboard.error.message : 'โหลดข้อมูลการปฏิบัติตามกฎระเบียบไม่สำเร็จ'}
        </div>
      ) : null}

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat
          label="คะแนนความสอดคล้องถ่วงน้ำหนัก"
          value={d ? d.score_percent.toFixed(2) : '—'}
          unit="%"
          foot={d ? (
            <span className="row" style={{ gap: 6 }}>
              <span style={{ color: scoreTone }}>{ragLabel[d.rag]}</span>
              {delta !== undefined ? (
                <span className={delta >= 0 ? 'sev sev-low' : 'sev sev-critical'} style={{ fontSize: 11 }}>
                  <i className="sev-dot" />
                  {delta >= 0 ? 'เพิ่มขึ้น' : 'ลดลง'} {Math.abs(delta / 100).toFixed(2)} จุด
                </span>
              ) : null}
            </span>
          ) : undefined}
        />
        <Stat label="เป็นไปตามกำหนด" value={d ? d.green : '—'} unit="ภาระ" foot="สถานะเขียวจากการคำนวณล่าสุด" />
        <Stat label="เฝ้าระวัง" value={d ? d.amber : '—'} unit="ภาระ" foot="ครบกำหนดภายใน 7 วันหรือหลักฐานล่าช้า" />
        <Stat
          label="เกินกำหนด"
          value={d ? <span style={{ color: d.overdue > 0 ? 'var(--color-crimson)' : undefined }}>{d.overdue}</span> : '—'}
          unit="ภาระ"
          foot={d ? `น้ำหนักรวม ${d.weight_total} · ภาระที่ต้องคิดคะแนน ${d.scored}` : undefined}
        />
      </div>

      <div className="grid-main" style={{ marginBottom: 16 }}>
        <div className="card card-pad">
          <div className="row" style={{ marginBottom: 14 }}>
            <span className="card-label" style={{ margin: 0 }}>คะแนนแยกตามหมวดภาระ</span>
            <span className="chip" style={{ marginLeft: 'auto' }}>{d ? d.obligations_total : rows.length} ภาระ</span>
          </div>
          {dashboard.isLoading ? <Skel h={190} /> : d && d.categories.length > 0 ? (
            <div className="stack" style={{ gap: 13 }}>
              {d.categories.map((cat) => {
                const tone = cat.score_bps >= 8500 ? 'var(--color-emerald)' : cat.score_bps >= 7000 ? 'var(--color-amber)' : 'var(--color-crimson)'
                return (
                  <div key={cat.category}>
                    <div className="row" style={{ gap: 10, marginBottom: 5 }}>
                      <span style={{ fontSize: 12.5 }}>{categoryLabel[cat.category] ?? cat.category}</span>
                      {cat.overdue > 0 ? <span className="chip" style={{ color: 'var(--color-crimson)' }}>เกินกำหนด {cat.overdue}</span> : null}
                      <span className="tnum dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>{(cat.score_bps / 100).toFixed(2)}%</span>
                    </div>
                    <span className="bar" style={{ display: 'block', width: '100%' }}>
                      <i style={{ width: `${cat.score_bps / 100}%`, background: tone }} />
                    </span>
                    <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                      {cat.obligations} ภาระ · น้ำหนัก {cat.weight_total}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="dim" style={{ fontSize: 12 }}>ยังไม่มีภาระที่เปิดอยู่ในทะเบียนขององค์กร</div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">แนวโน้มคะแนนจากจุดบันทึก</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 34, marginTop: 12, color: scoreTone }}>
            {d ? `${d.score_percent.toFixed(2)}%` : '—'}
          </div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
            {d ? `${d.total - d.dormant} ภาระที่มีกำหนด · ${d.weight_total} คะแนนน้ำหนัก` : 'กำลังคำนวณ'}
          </div>
          {d ? <TrendBars trend={d.trend} /> : <Skel h={120} />}
          <div className="dim" style={{ fontSize: 10.5, marginTop: 10, lineHeight: 1.7 }}>
            คะแนนเขียวตั้งแต่ 85% ขึ้นไป · เหลือง 70–84.99% · แดงต่ำกว่า 70%
          </div>
        </div>
      </div>

      {evidenceFor ? (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="row" style={{ marginBottom: 10 }}>
            <div>
              <div className="card-label">แนบหลักฐานประกอบภาระ</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginTop: 5 }}>{evidenceFor.title}</div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                {evidenceFor.authority} · {evidenceFor.legal_ref}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setEvidenceFor(null)}>ปิด</button>
          </div>

          <div className="grid-2">
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="compliance-period">งวดที่ครบกำหนด</label>
              <input id="compliance-period" className="input tnum" type="date" value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)} />
              <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                ระบบกำหนดงวดจากกำหนดที่ยังไม่มีหลักฐาน การแนบหลักฐานจะปิดงวดนี้และเลื่อนไปงวดถัดไป
              </div>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="compliance-kind">ประเภทหลักฐาน</label>
              <select id="compliance-kind" className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
                {Object.entries(kindLabel).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid-2">
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="compliance-ref">เลขที่อ้างอิง</label>
              <input id="compliance-ref" className="input" maxLength={120} value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
                placeholder="เช่น ภพ30-2569-10-0001 หรือเลขที่ใบเสร็จ" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="compliance-submitted">วันที่นำส่ง</label>
              <input id="compliance-submitted" className="input tnum" type="date" value={submittedOn}
                onChange={(e) => setSubmittedOn(e.target.value)} />
            </div>
          </div>

          <div className="field">
            <label htmlFor="compliance-note">บันทึกเพิ่มเติม</label>
            <textarea id="compliance-note" className="input" rows={3} maxLength={2000} value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{ width: '100%', height: 'auto', padding: '10px 12px' }}
              placeholder="ช่องทางที่ยื่น ผู้รับผิดชอบ หรือข้อสังเกตจากการตรวจสอบ" />
          </div>

          {attach.isError ? (
            <div className="form-error">
              {attach.error instanceof Error ? attach.error.message : 'แนบหลักฐานไม่สำเร็จ'}
            </div>
          ) : null}

          <div className="row" style={{ marginTop: 14, gap: 10 }}>
            <button className="btn btn-primary" disabled={attach.isPending || referenceNo.trim().length === 0}
              onClick={() => evidenceFor && attach.mutate(evidenceFor)}>
              {attach.isPending ? 'กำลังบันทึก…' : 'บันทึกหลักฐาน'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              หลักฐานที่บันทึกแล้วแก้ไขไม่ได้ เพื่อรักษาร่องรอยการตรวจสอบ
            </span>
          </div>
        </div>
      ) : null}

      <div className="row" style={{ marginBottom: 10 }}>
        <span className="card-label" style={{ margin: 0 }}>ทะเบียนภาระตามกฎหมาย</span>
        <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} รายการ</span>
      </div>

      {obligations.isLoading ? <Skel h={260} /> : rows.length === 0 ? (
        <div className="card card-pad dim" style={{ fontSize: 12.5 }}>
          ยังไม่มีทะเบียนภาระสำหรับองค์กรนี้ — เมื่อมีทะเบียน ระบบจะคำนวณกำหนดถัดไปและคะแนนถ่วงน้ำหนักให้อัตโนมัติ
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table data">
            <thead>
              <tr>
                <th>ภาระตามกฎหมาย</th>
                <th>หน่วยงานกำกับดูแล</th>
                <th>รอบกำหนด</th>
                <th>กำหนดถัดไป</th>
                <th>หลักฐานล่าสุด</th>
                <th>สถานะ</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Fragment key={row.id}>
                  <tr onClick={() => setSelected(selected === row.id ? null : row.id)}>
                    <td style={{ maxWidth: 360 }}>
                      <div style={{ lineHeight: 1.5 }}>{row.title}</div>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                        {row.code} · น้ำหนัก {row.weight} · {criticalityLabel[row.criticality]}
                      </div>
                    </td>
                    <td style={{ maxWidth: 250 }}>
                      <div style={{ fontSize: 12 }}>{row.authority}</div>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 3, lineHeight: 1.5 }}>{row.legal_ref}</div>
                    </td>
                    <td>
                      <span className="chip">{cadenceLabel[row.cadence]}</span>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 5 }}>{cadenceHint(row)}</div>
                    </td>
                    <td>
                      <div className="tnum" style={{ fontSize: 12.5 }}>{row.next_due_on ? thaiDate(row.next_due_on) : '—'}</div>
                      <span className={`sev ${dueTone(row)}`} style={{ fontSize: 10.5, marginTop: 4 }}>
                        {!row.dormant ? <i className="sev-dot" /> : null}
                        {dueText(row)}
                      </span>
                    </td>
                    <td style={{ maxWidth: 220 }}>
                      {row.last_evidence ? (
                        <>
                          <div className="mono" style={{ fontSize: 11.5 }}>{row.last_evidence.reference_no}</div>
                          <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                            {kindLabel[row.last_evidence.evidence_kind] ?? row.last_evidence.evidence_kind} · {thaiDate(row.last_evidence.submitted_on)}
                          </div>
                          {row.late ? <span className="chip" style={{ marginTop: 5, color: 'var(--color-amber)' }}>ยื่นล่าช้า</span> : null}
                        </>
                      ) : (
                        <span className="dim" style={{ fontSize: 11.5 }}>ยังไม่มีหลักฐาน</span>
                      )}
                    </td>
                    <td>
                      {row.dormant ? <span className="chip">ยังไม่เปิดกำหนด</span> : <RagBadge rag={row.rag} />}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {canAttach && !row.dormant && row.days_until <= 90 ? (
                        <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); openEvidence(row) }}>
                          แนบหลักฐาน
                        </button>
                      ) : (
                        <span className="dim">{row.days_until > 90 ? 'ยังไม่ถึงกำหนด' : '—'}</span>
                      )}
                    </td>
                  </tr>

                  {selected === row.id ? (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--color-wash)', padding: 0 }}>
                        <div style={{ padding: '18px 20px' }}>
                          <div className="grid-2">
                            <div>
                              <div className="card-label">ฐานกฎหมายและหน่วยงาน</div>
                              <div style={{ fontSize: 12.5, lineHeight: 1.8, marginTop: 7 }}>
                                <div>{row.authority}</div>
                                <div className="dim">{row.legal_ref}</div>
                              </div>
                              <div className="card-label" style={{ marginTop: 16 }}>รอบและกำหนด</div>
                              <div style={{ fontSize: 12.5, lineHeight: 1.8, marginTop: 7 }}>
                                <div>{cadenceHint(row)}</div>
                                <div className="dim">
                                  {row.grace_days > 0 ? `ผ่อนผันเพิ่ม ${row.grace_days} วัน · ` : ''}
                                  ผู้รับผิดชอบตามบทบาท: {roleLabel[row.owner_role ?? ''] ?? 'ผู้จัดการตรวจสอบ'}
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className="card-label">รายละเอียดภาระ</div>
                              <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, marginTop: 7, whiteSpace: 'pre-wrap' }}>
                                {row.notes || 'ไม่มีบันทึกเพิ่มเติม'}
                              </p>
                              <div className="card-label" style={{ marginTop: 14 }}>การให้คะแนน</div>
                              <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 6 }}>
                                น้ำหนัก {row.weight} จาก 5 · สถานะ {ragLabel[row.rag]} · คะแนนที่ได้รับ {(row.attainment_bps / 100).toFixed(0)}%
                              </div>
                              {row.last_evidence ? (
                                <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 8 }}>
                                  หลักฐานล่าสุด {row.last_evidence.reference_no} สำหรับงวด {thaiDate(row.last_evidence.period_end)}
                                  {row.last_evidence.note ? ` · ${row.last_evidence.note}` : ''}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid-2" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <div className="card-label">กำหนดที่ต้องติดตาม</div>
          <div style={{ marginTop: 10 }}>
            {(d?.next_deadlines ?? []).length === 0 ? (
              <div className="dim" style={{ fontSize: 12 }}>ไม่มีกำหนดที่เปิดอยู่</div>
            ) : (d?.next_deadlines ?? []).map((row) => (
              <div key={row.id} style={{
                display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 12,
                padding: '11px 0', borderTop: '1px solid var(--color-hairline)', alignItems: 'start',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>{row.title}</div>
                  <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                    {row.code} · {row.authority}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="tnum" style={{ fontSize: 11.5 }}>{row.next_due_on ? thaiDate(row.next_due_on) : '—'}</div>
                  <span className={`sev ${dueTone(row)}`} style={{ fontSize: 10.5, marginTop: 4 }}>
                    {!row.dormant ? <i className="sev-dot" /> : null}
                    {dueText(row)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card card-pad">
          <div className="card-label">หลักฐานล่าสุด</div>
          <div style={{ marginTop: 10 }}>
            {(d?.recent_evidence ?? []).length === 0 ? (
              <div className="dim" style={{ fontSize: 12 }}>ยังไม่มีหลักฐานที่บันทึกไว้</div>
            ) : (d?.recent_evidence ?? []).map((ev) => (
              <div key={ev.id} style={{
                padding: '11px 0', borderTop: '1px solid var(--color-hairline)',
              }}>
                <div className="row" style={{ gap: 8 }}>
                  <span style={{ fontSize: 12.5 }}>{ev.obligation_title}</span>
                  <span className="chip" style={{ marginLeft: 'auto' }}>{kindLabel[ev.evidence_kind] ?? ev.evidence_kind}</span>
                </div>
                <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                  {ev.reference_no} · งวด {thaiDate(ev.period_end)} · นำส่ง {thaiDate(ev.submitted_on)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="dim" style={{ marginTop: 20, fontSize: 11, lineHeight: 1.8, maxWidth: 760 }}>
        คะแนนคำนวณจากน้ำหนักของแต่ละภาระคูณระดับความสำเร็จ: เป็นไปตามกำหนด 100% · เฝ้าระวัง 60% ·
        หลักฐานล่าช้า 70% · เกินกำหนด 0% ภาระตามเหตุการณ์ที่ยังไม่เกิดไม่ถูกนำมาคิดคะแนน
        เพื่อไม่ให้เหตุที่ยังไม่เกิดขึ้นทำให้องค์กรดูดีหรือดูแย่เกินจริง
      </div>
    </>
  )
}
